/**
 * 前端配置管理模块
 * 统一管理所有服务地址和端口配置
 */

// 获取当前环境
const getEnvironment = () => {
  return process.env.NODE_ENV || 'development';
};

// 根据环境获取配置
const getConfig = () => {
  const env = getEnvironment();
  
  // 开发环境配置
  if (env === 'development') {
    // 如果设置了空字符串，说明是代理模式（用于ngrok远程访问）
    // 此时使用相对路径，请求会通过React开发服务器代理到本地后端
    const envApi = process.env.REACT_APP_API_URL;
    const useProxy = envApi === '';
    const h = typeof window !== 'undefined' ? window.location.hostname : '';
    const pageIsLan = !!(h && h !== 'localhost' && h !== '127.0.0.1');
    const envPointsToLoopback = (s) => !!(s && /localhost|127\.0\.0\.1/.test(s));
    const envResourceHost = (s) => {
      if (!s || !/^https?:\/\//i.test(s)) return null;
      try {
        return new URL(s).hostname;
      } catch {
        return null;
      }
    };
    // 换 WiFi / 多网卡时 .env 里常残留上一段网络的 IP，页面却是新 IP → 列表能靠缓存假象「能开」，视频直连旧 IP 必挂
    const envHostConflictsPage = (s) =>
      pageIsLan && !!s && (
        envPointsToLoopback(s) ||
        (!!envResourceHost(s) && envResourceHost(s) !== h)
      );
    // 前端与 API 分属两台机器时设为 true，禁止「页面主机名」覆盖 REACT_APP_API_URL
    const strictLanApiUrl = process.env.REACT_APP_STRICT_API_URL === 'true';

    let apiBase = '';
    if (useProxy) {
      apiBase = '';
    } else if (envApi) {
      if (!strictLanApiUrl && pageIsLan && envHostConflictsPage(envApi)) {
        if (!envPointsToLoopback(envApi) && envResourceHost(envApi) && envResourceHost(envApi) !== h) {
          console.warn(
            `[config] REACT_APP_API_URL 指向 ${envResourceHost(envApi)}，与当前页面主机 ${h} 不一致，已改为 http://${h}:8000（换网后请更新 .env 或删除该项）`
          );
        }
        apiBase = `http://${h}:8000`;
      } else {
        apiBase = envApi;
      }
    } else if (typeof window !== 'undefined') {
      // 手机/局域网用 http://电脑IP:3000 打开且未配 REACT_APP_API_URL 时，避免请求打到手机自己的 localhost:8000
      if (pageIsLan) {
        apiBase = `http://${h}:8000`;
      } else {
        apiBase = 'http://localhost:8000';
      }
    } else {
      apiBase = 'http://localhost:8000';
    }
    const apiOrigin = apiBase.replace(/\/$/, '');

    const staticBase = useProxy
      ? '/static'
      : (() => {
          const fromEnv = process.env.REACT_APP_STATIC_URL;
          const rejectEnv = strictLanApiUrl ? envPointsToLoopback : envHostConflictsPage;
          if (fromEnv && !rejectEnv(fromEnv)) {
            return fromEnv.replace(/\/$/, '');
          }
          return `${apiOrigin}/static`;
        })();
    const videosBase = useProxy
      ? '/static/videos'
      : (() => {
          const fromEnv = process.env.REACT_APP_VIDEOS_URL;
          const rejectEnv = strictLanApiUrl ? envPointsToLoopback : envHostConflictsPage;
          if (fromEnv && !rejectEnv(fromEnv)) {
            return fromEnv.replace(/\/$/, '');
          }
          return `${apiOrigin}/static/videos`;
        })();
    const imagesBase = useProxy
      ? '/static/images'
      : (() => {
          const fromEnv = process.env.REACT_APP_IMAGES_URL;
          const rejectEnv = strictLanApiUrl ? envPointsToLoopback : envHostConflictsPage;
          if (fromEnv && !rejectEnv(fromEnv)) {
            return fromEnv.replace(/\/$/, '');
          }
          return `${apiOrigin}/static/images`;
        })();

    return {
      API_BASE_URL: apiBase,
      STATIC_BASE_URL: staticBase,
      FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000',
      VIDEOS_URL: videosBase,
      IMAGES_URL: imagesBase,
      // 超时配置 - 增加超时时间以配合后台处理
      REQUEST_TIMEOUT: parseInt(process.env.REACT_APP_REQUEST_TIMEOUT) || 30000,
      // 重试配置
      MAX_RETRIES: parseInt(process.env.REACT_APP_MAX_RETRIES) || 3,
    };
  }
  
  // 生产环境配置
  if (env === 'production') {
    // 检查是否有 REACT_APP_API_URL 环境变量（即使是空字符串也要使用）
    const hasApiUrl = 'REACT_APP_API_URL' in process.env;
    const apiUrl = hasApiUrl ? process.env.REACT_APP_API_URL : 'https://api.your-domain.com';
    
    return {
      API_BASE_URL: apiUrl,
      STATIC_BASE_URL: process.env.REACT_APP_STATIC_URL || '/static',
      FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000',
      AI_SERVICE_URL: process.env.REACT_APP_AI_SERVICE_URL,
      VIDEOS_URL: process.env.REACT_APP_VIDEOS_URL || '/static/videos',
      IMAGES_URL: process.env.REACT_APP_IMAGES_URL || '/static/images',
      REQUEST_TIMEOUT: parseInt(process.env.REACT_APP_REQUEST_TIMEOUT) || 30000,
      MAX_RETRIES: parseInt(process.env.REACT_APP_MAX_RETRIES) || 3,
    };
  }
  
  // 测试环境配置
  if (env === 'test') {
    return {
      API_BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      STATIC_BASE_URL: process.env.REACT_APP_STATIC_URL || 'http://localhost:8000/static',
      FRONTEND_URL: process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000',
      VIDEOS_URL: process.env.REACT_APP_VIDEOS_URL || 'http://localhost:8000/static/videos',
      IMAGES_URL: process.env.REACT_APP_IMAGES_URL || 'http://localhost:8000/static/images',
      REQUEST_TIMEOUT: parseInt(process.env.REACT_APP_REQUEST_TIMEOUT) || 5000,
      MAX_RETRIES: parseInt(process.env.REACT_APP_MAX_RETRIES) || 2,
    };
  }
  
  // 默认开发环境配置
  return {
    API_BASE_URL: 'http://localhost:8000',
    STATIC_BASE_URL: 'http://localhost:8000/static',
    FRONTEND_URL: 'http://localhost:3000',
    VIDEOS_URL: 'http://localhost:8000/static/videos',
    IMAGES_URL: 'http://localhost:8000/static/images',
    REQUEST_TIMEOUT: 10000,
    MAX_RETRIES: 3,
  };
};

// 导出配置
export const config = getConfig();

// 导出工具函数
export const getVideoUrl = (filename, addTimestamp = false) => {
  // 对文件名进行URL编码，避免中文、空格导致的读取失败
  const safe = encodeURIComponent(filename || '');
  let url = `${config.VIDEOS_URL}/${safe}`;
  // 如果需要添加时间戳来避免缓存（用于强制刷新）
  if (addTimestamp) {
    url += `?_t=${Date.now()}`;
  }
  return url;
};

export const getImageUrl = (filename) => {
  const safe = encodeURIComponent(filename || '');
  return `${config.IMAGES_URL}/${safe}`;
};

export const getStaticUrl = (path) => {
  return `${config.STATIC_BASE_URL}/${path}`;
};

export const getAudioUrl = (filename) => {
  const safe = encodeURIComponent(filename || '');
  // 如果使用代理模式（API_BASE_URL为空），使用相对路径
  if (config.API_BASE_URL === '') {
    return `/static/audio/${safe}`;
  }
  return `${config.API_BASE_URL}/static/audio/${safe}`;
};

// 导出环境信息
export const environment = getEnvironment();

// 导出是否为开发环境
export const isDevelopment = environment === 'development';
export const isProduction = environment === 'production';
export const isTest = environment === 'test';

export default config;
